// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Login Page
 *
 * With CIAM authentication, this page acts as a gateway:
 * - If the user is already authenticated (MSAL has an active account),
 *   redirect to the app.
 * - Otherwise, trigger a redirect to the Azure Entra External ID login page.
 *
 * The email/password form has been replaced by the CIAM redirect flow, so the
 * "Sign In" button hands off to the CIAM-hosted login screen rather than
 * collecting credentials here. Styling mirrors the Helios Operator Console
 * mockup and uses IBM Carbon components.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Button, InlineNotification } from '@carbon/react';
import { ArrowRight } from '@carbon/icons-react';
import { loginRequest } from '../config/authConfig';
import './Login.css';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();
  const [error, setError] = useState<string | null>(null);

  // If already authenticated, redirect to the intended page
  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Trigger CIAM login redirect
  const handleLogin = async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      setError((err as Error).message || 'Failed to initiate sign-in. Please try again.');
    }
  };

  // Don't render the login UI if we're already authenticated (avoids flash)
  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <p className="login-eyebrow">Sign in to</p>
          <h1 className="login-brand">Helios</h1>
        </div>

        {error && (
          <InlineNotification
            className="login-error"
            kind="error"
            lowContrast
            title="Sign-in failed"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
          />
        )}

        <p className="login-info">Sign in with your organizational account to continue.</p>

        <Button className="login-button" renderIcon={ArrowRight} onClick={handleLogin}>
          Sign In
        </Button>
      </div>
    </div>
  );
}

export default Login;
